import { api } from './api'

// const API_BASE_URL = process.env.REACT_APP_API_URL!

export interface SubscriptionPlan {
  priceId: string
  name: string
  price: number
  pricePerMonth?: number
  currency: string
  interval: string
  savings?: number
  savingsPercent?: number
  popular: boolean
  features: string[]
}

export interface CreditPack {
  credits: number
  price: number
  priceId: string
  savings?: number
  popular?: boolean
}

export interface PricingData {
  subscriptions: {
    essential: SubscriptionPlan
    professional: SubscriptionPlan
    enterprise: SubscriptionPlan
    essentialAnnual: SubscriptionPlan
    professionalAnnual: SubscriptionPlan
    enterpriseAnnual: SubscriptionPlan
  }
  creditPacks: {
    [key: string]: CreditPack
  }
}

export interface CheckoutSessionResponse {
  sessionId: string
  url: string
}

export interface UserSubscription {
  tier: string
  tierName: string
  billingPeriod: 'MONTHLY' | 'ANNUAL' | null
  status: 'ACTIVE' | 'CANCELED' | 'PAST_DUE' | 'INCOMPLETE' | 'TRIALING' | null
  stripeSubscriptionId: string | null
  currentPeriodEnd: string | null
  startDate: string | null
  nextRenewal: string | null
  monthlyCredits: number
  rolloverLimit: number | null
  features: string[]
  isActive: boolean
  isPastDue: boolean
  requiresAction: boolean
  lastUpdated: string
  updatedAt: string
}

export interface UserCredits {
  userId: string
  creditBalance: number
  lifetimeCreditsSpent: number
  lifetimeCreditsPurchased: number
  createdAt: string
  updatedAt: string
}

export interface CreditTransaction {
  transactionId: string
  userId: string
  type: 'purchase' | 'spend' | 'refund' | 'bonus'
  amount: number
  balanceAfter: number
  stripePaymentIntentId?: string
  packageType?: string
  priceGBP?: number
  propertyId?: string
  description?: string
  createdAt: string
}

class PaymentService {
  /**
   * Get pricing information
   */
  async getPricing(): Promise<PricingData> {
    const response = await api.get('/payments/pricing')
    return response.data
  }

  /**
   * Create generic checkout session (for credits or subscription)
   * Used by BillingPage - backend determines pricing
   */
  async createCheckout(
    type: 'credits' | 'subscription',
    credits?: number,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<{ url: string; sessionId?: string }> {
    const response = await api.post('/payments/checkout', {
      type,
      credits,
      successUrl: successUrl || `${window.location.origin}/success`,
      cancelUrl: cancelUrl || `${window.location.origin}/pricing`
    })

    return response.data
  }

  /**
   * Create subscription checkout session
   * @param priceId - Stripe price ID
   * @param couponId - Optional launch discount coupon ID (e.g., L40PROM)
   */
  async createSubscriptionCheckout(
    priceId: string,
    couponId?: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<CheckoutSessionResponse> {
    const response = await api.post('/payments/checkout', {
      type: 'subscription',
      productId: priceId,  // Send priceId as productId for backend to process
      couponId,  // Launch discount coupon
      successUrl: successUrl || `${window.location.origin}/success`,
      cancelUrl: cancelUrl || `${window.location.origin}/pricing`
    })

    return response.data
  }

  /**
   * Create credit pack checkout session
   */
  async createCreditPackCheckout(
    priceId: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<CheckoutSessionResponse> {
    const response = await api.post('/payments/checkout', {
      type: 'credits',
      productId: priceId,  // Send priceId as productId for backend to process
      successUrl: successUrl || `${window.location.origin}/success`,
      cancelUrl: cancelUrl || `${window.location.origin}/pricing`
    })
    return response.data
  }

  /**
   * Get current subscription
   */
  async getSubscription(): Promise<{
    subscription: UserSubscription | null
    hasActiveSubscription: boolean
    canAccessUnlimitedDeals: boolean
  }> {
    // Subscription info is included in credits/balance response
    const response = await api.get('/credits/balance')
    const data = response.data

    // Transform the response to match expected format
    return {
      subscription: data.subscription || null,
      hasActiveSubscription: data.subscription?.status === 'ACTIVE',
      canAccessUnlimitedDeals: data.credits?.tier === 'UNLIMITED' || data.subscription?.status === 'ACTIVE'
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(cancelAtPeriodEnd: boolean = true): Promise<void> {
    await api.delete('/credits/subscription', {
      data: { cancelAtPeriodEnd }
    })
  }

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(): Promise<void> {
    await api.post('/payments/subscription/reactivate', {})
  }

  /**
   * Change subscription plan
   */
  async changeSubscription(newSubscriptionType: 'monthly' | 'annual'): Promise<void> {
    await api.post('/payments/subscription/change', { newSubscriptionType })
  }

  /**
   * Get credit balance
   */
  async getCredits(): Promise<{
    credits: UserCredits
    recentTransactions: CreditTransaction[]
  }> {
    const response = await api.get('/payments/credits')
    return response.data
  }

  /**
   * Check if user can unlock property
   */
  async canUnlock(): Promise<{
    canUnlock: boolean
    hasSubscription: boolean
    creditBalance: number
    unlockMethod: 'subscription' | 'credit'
  }> {
    const response = await api.get('/payments/can-unlock')
    return response.data
  }

  /**
   * Unlock property
   */
  async unlockProperty(propertyId: string): Promise<{
    success: boolean
    method: 'subscription' | 'credit'
    creditsRemaining?: number
  }> {
    const response = await api.post('/payments/unlock', { propertyId })
    return response.data
  }

  /**
   * Create customer portal session
   */
  async createPortalSession(returnUrl?: string): Promise<{ url: string }> {
    const response = await api.post('/payments/portal', {
      returnUrl: returnUrl || window.location.origin
    })
    return response.data
  }

  /**
   * Redirect to Stripe Checkout
   */
  async redirectToCheckout(sessionUrl: string): Promise<void> {
    window.location.href = sessionUrl
  }

  /**
   * Redirect to Customer Portal
   */
  async redirectToPortal(returnUrl?: string): Promise<void> {
    const { url } = await this.createPortalSession(returnUrl)
    window.location.href = url
  }

  /**
   * Get customer portal URL
   */
  async getCustomerPortal(returnUrl?: string): Promise<{ url: string }> {
    return this.createPortalSession(returnUrl)
  }

  /**
   * Get payment receipts and invoices
   */
  async getReceipts(): Promise<{ receipts: any[]; invoices: any[] }> {
    const response = await api.get('/payments/receipts')
    return response.data
  }

  /**
   * Download receipt as PDF
   */
  async downloadReceipt(receiptId: string): Promise<ArrayBuffer> {
    const response = await api.get(`/payments/receipts/${receiptId}/download`, {
      responseType: 'arraybuffer'
    })
    return response.data
  }
}

export const paymentService = new PaymentService()
